import * as React from 'react';
import {
  Button,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  Tab,
  TabList,
  tokens,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { BuildWithAITab } from '../pages/BuildWithAI/Components/BuildWithAITab';
import type { NavigationTab } from './TabNavigationTypes';

export interface TabNavigationProps {
  tabs: Array<NavigationTab>;
}

export function TabNavigation({ tabs }: TabNavigationProps) {
  const history = useHistory();
  const intl = useIntl();
  const [selectedTabId, setSelectedTabId] = React.useState<string>('/' + history.location.pathname.split('/')?.[1]);

  React.useEffect(() => {
    const newValue = history.location.pathname.split('/')?.[1];
    if (selectedTabId !== newValue) {
      if (newValue === 'expert' && !tabs.some((tab) => tab.id === '/expert')) {
        setSelectedTabId('/guidance');
      } else {
        setSelectedTabId('/' + history.location.pathname.split('/')?.[1]);
      }
    }
  }, [history.location.pathname, selectedTabId]);

  const handleSelectTab = (tabId: string) => {
    history.push(tabId);
    setSelectedTabId(tabId);
  };

  return (
    <Overflow>
      <TabList
        onTabSelect={(_, data) => handleSelectTab(data.value as string)}
        size="medium"
        selectedValue={selectedTabId}
        aria-label={intl.formatMessage(mainNav)}
      >
        {tabs.map((tab) => {
          switch (tab.id) {
            case '/ai':
              return <BuildWithAITab key={tab.id} tabs={tabs} tab={tab} selectedTabId={selectedTabId} />;
            default:
              return (
                <OverflowItem key={tab.id} id={tab.id} priority={tab.id === selectedTabId ? 2 : 1}>
                  <Tab
                    value={tab.id}
                    icon={tab.id === selectedTabId ? tab.iconFilled : tab.iconRegular}
                    style={tabs.indexOf(tab) === 0 ? { paddingLeft: 0 } : {}}
                  >
                    {tab.name}
                  </Tab>
                </OverflowItem>
              );
          }
        })}
        <OverflowMenu onTabSelect={handleSelectTab} tabs={tabs} />
      </TabList>
    </Overflow>
  );
}

//----- OverflowMenuItem -----//
interface OverflowMenuItemProps {
  tab: NavigationTab;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}

/**
 * A menu item for an overflow menu that only displays when the tab is not visible
 */
const OverflowMenuItem = (props: OverflowMenuItemProps) => {
  const { tab, onClick } = props;
  const isVisible = useIsOverflowItemVisible(tab.id);

  if (isVisible) {
    return null;
  }

  return (
    <MenuItem key={tab.id} onClick={onClick} icon={tab.iconRegular}>
      <div>{tab.name}</div>
    </MenuItem>
  );
};

//----- OverflowMenu -----//
const useOverflowMenuStyles = makeStyles({
  menu: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  menuButton: {
    alignSelf: 'center',
  },
});

interface OverflowMenuProps {
  onTabSelect?: (tabId: string) => void;
  tabs: Array<NavigationTab>;
}

/**
 * A menu for selecting tabs that have overflowed and are not visible.
 */
const OverflowMenu = (props: OverflowMenuProps) => {
  const intl = useIntl();
  const { onTabSelect, tabs } = props;
  const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();

  const classes = useOverflowMenuStyles();

  const onItemClick = (tabId: string) => {
    onTabSelect?.(tabId);
  };

  if (!isOverflowing) {
    return null;
  }

  return (
    <Menu hasIcons>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="transparent"
          className={classes.menuButton}
          ref={ref}
          icon={<MoreHorizontalRegular />}
          aria-label={intl.formatMessage(moreTabsLabel, {
            overflowCount,
          })}
          role="tab"
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList className={classes.menu}>
          {tabs.map((tab) => (
            <OverflowMenuItem key={tab.id} tab={tab} onClick={() => onItemClick(tab.id)} />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

const mainNav = defineMessage({
  defaultMessage: 'Main navigation',
  description: 'Label to indicate the Main Navigation drawer',
});

const moreTabsLabel = defineMessage({
  defaultMessage: '{overflowCount} more tabs',
  description: 'aria-label for the overflow menu button describing the number of tabs that are not visible',
});
